<script lang="ts" setup>
import {
    faBracketsCurly,
    faCode,
    faEye,
    faLanguage,
    faSlash,
    faSpinnerThird,
    faStar,
} from "@fortawesome/pro-regular-svg-icons";
import { faBolt, faSave, faPaperPlane } from "@fortawesome/pro-solid-svg-icons";
import type { FieldDefinition } from "~/models/Automation";

const { canManageTemplates, isSuperAdmin, user } = useUser();
const store = useMailStore();
const detectedChanges = ref<boolean>(false);

const editingField = ref();
const deletable = ref();
const sideView = ref<"fields" | "template" | "language" | null>(null);
const showTestModal = ref<boolean>(false);
const preview = ref<boolean>(false);
const previewModalLocale = ref(
    user.value?.account?.locale ?? store.previewMail?.locale,
);
const previewModalEmail = computed(
    () =>
        store.previewMail?.translations?.find(
            (translation) =>
                translation.locale?.id === previewModalLocale.value?.id,
        ) ?? store.previewMail,
);

async function togglePreview() {
    if (store.hasChanges()) {
        store.saveMail().finally(() => {
            preview.value = true;
        });
    } else {
        preview.value = true;
    }
}

function onBeforeUnload(event: BeforeUnloadEvent) {
    if (store.hasChanges()) {
        event.preventDefault();
        event.returnValue = true;
        return "You have unsaved changes. Save before leaving.";
    }
}

async function onBeforeSend(): Promise<void> {
    await store.saveMail();
    return;
}

function created(new_field: FieldDefinition) {
    store.metadata = {
        ...store.metadata,
        fields: [new_field, ...store.metadata.fields],
    };
}

function update(field: { to: FieldDefinition; from: FieldDefinition }) {
    const fields = store.metadata.fields;
    const index = fields.findIndex((f) => f.key === field.from.key);
    fields[index] = field.to;
    store.metadata = {
        ...store.metadata,
        fields: fields,
    };
}

function deleteField() {
    const initialFields = [...store.metadata.fields];

    const fields = store.metadata.fields;
    const index = fields.findIndex(
        (field) => field.key === deletable.value.key,
    );
    fields.splice(index, 1);
    store.metadata.fields = fields;

    useToast().add({
        title: `Field Deleted`,
        description: `${deletable.value.label ?? deletable.value.key}`,
        actions: [
            {
                label: "Undo",
                click: () => {
                    store.metadata.fields = initialFields;
                },
            },
        ],
    });
    deletable.value = undefined;
}

onBeforeMount(() => {
    store.loadMailById(useRoute().params.id as string).catch(() => {
        navigateTo({ name: "emails" });
    });
});

onBeforeRouteLeave((to, from, next) => {
    if (detectedChanges.value) {
        return;
    }

    if (store.hasChanges()) {
        detectedChanges.value = true;
        useToast().add({
            title: "Unsaved Changes",
            description: "You have unsaved changes. Save before leaving.",
            icon: "i-heroicons-exclamation-triangle",
            color: "orange",
            timeout: 0,
            actions: [
                {
                    label: "Save",
                    click: () => {
                        store
                            .saveMail()
                            .then(() => next())
                            .finally(() => (detectedChanges.value = false));
                    },
                    color: "primary",
                },
                {
                    label: "Discard",
                    click: () => next(),
                },
            ],
            callback: () => (detectedChanges.value = false),
        });
    } else {
        next();
    }
});

onMounted(() => {
    window.addEventListener("beforeunload", onBeforeUnload);
});

onBeforeUnmount(() => {
    window.removeEventListener("beforeunload", onBeforeUnload);
});
</script>

<template>
    <AppBuilderPage
        :breadcrumbs="[{ to: { name: 'emails' } }]"
        :loading="store.loading || store.loading === null"
        label="Emails"
        title-class="h-9"
    >
        <template #title>
            <UInput
                v-model="store.name"
                class="w-96 font-normal font-base"
                size="md"
            />
        </template>
        <template #actions>
            <div class="flex-1 flex items-center justify-center" />
            <div class="flex items-center justify-end gap-2">
                <div class="text-sm mr-2">
                    <template v-if="store.saving"> Saving your email </template>
                    <template v-else-if="store.lastUpdatedAtDisplay">
                        Last updated {{ store.lastUpdatedAtDisplay }}
                    </template>
                </div>

                <UButton
                    :disabled="store.saving"
                    class="h-9"
                    size="lg"
                    @click="store.saveMail"
                >
                    <FontAwesomeIcon
                        :icon="store.saving ? faSpinnerThird : faSave"
                        :spin="store.saving"
                    />
                    <span>Save</span>
                </UButton>

                <UButtonGroup>
                    <UTooltip
                        :popper="{ placement: 'top' }"
                        text="Preview (saves email before previewing)"
                    >
                        <UButton
                            :disabled="store.saving"
                            class="normal-case flex items-center justify-center w-9 h-9"
                            color="white"
                            size="md"
                            square
                            @click="togglePreview"
                        >
                            <FontAwesomeIcon :icon="faEye" />
                        </UButton>
                    </UTooltip>
                    <UTooltip
                        :popper="{ placement: 'top' }"
                        text="Send test mail"
                    >
                        <UButton
                            class="w-9 h-9 justify-center items-center"
                            color="white"
                            size="lg"
                            square
                            @click="showTestModal = !showTestModal"
                        >
                            <FontAwesomeIcon :icon="faPaperPlane" />
                        </UButton>
                    </UTooltip>
                </UButtonGroup>

                <AppBrandSelector v-model:brand="store.brand" />

                <UButtonGroup>
                    <UTooltip
                        :popper="{ placement: 'top' }"
                        :text="
                            store.builderConfig.showValues
                                ? 'Hide variable values'
                                : 'Show variable values'
                        "
                    >
                        <UButton
                            class="w-9 h-9 justify-center items-center"
                            color="white"
                            size="lg"
                            square
                            @click="
                                store.builderConfig.showValues =
                                    !store.builderConfig.showValues
                            "
                        >
                            <FontAwesomeLayers fixed-width>
                                <FontAwesomeIcon :icon="faBolt" />
                                <FontAwesomeIcon
                                    v-if="!store.builderConfig.showValues"
                                    :icon="faSlash"
                                />
                            </FontAwesomeLayers>
                        </UButton>
                    </UTooltip>
                    <UTooltip
                        v-if="
                            store.mail.type === 'html' ||
                            canManageTemplates ||
                            isSuperAdmin
                        "
                        :popper="{ placement: 'top' }"
                        :text="
                            store.builderConfig.codeEditorVisible
                                ? 'Hide code editor'
                                : 'Show code editor'
                        "
                    >
                        <UButton
                            class="w-9 h-9 justify-center items-center"
                            color="white"
                            size="lg"
                            square
                            @click="
                                store.builderConfig.codeEditorVisible =
                                    !store.builderConfig.codeEditorVisible
                            "
                        >
                            <FontAwesomeLayers fixed-width>
                                <FontAwesomeIcon :icon="faCode" />
                                <FontAwesomeIcon
                                    v-if="
                                        !store.builderConfig.codeEditorVisible
                                    "
                                    :icon="faSlash"
                                />
                            </FontAwesomeLayers>
                        </UButton>
                    </UTooltip>
                </UButtonGroup>

                <UButtonGroup>
                    <UTooltip
                        :popper="{ placement: 'top' }"
                        :text="
                            sideView === 'fields'
                                ? 'Hide variable list'
                                : 'Show variable list'
                        "
                    >
                        <UButton
                            :disabled="store.saving"
                            class="normal-case flex items-center justify-center w-9 h-9"
                            color="white"
                            size="md"
                            square
                            @click="
                                sideView =
                                    sideView === 'fields' ? null : 'fields'
                            "
                        >
                            <FontAwesomeLayers fixed-width>
                                <FontAwesomeIcon :icon="faBracketsCurly" />
                                <FontAwesomeIcon
                                    v-if="sideView !== 'fields'"
                                    :icon="faSlash"
                                />
                            </FontAwesomeLayers>
                        </UButton>
                    </UTooltip>

                    <UTooltip
                        v-if="canManageTemplates"
                        :popper="{ placement: 'top' }"
                        text="Manage Template"
                    >
                        <UButton
                            :disabled="store.saving"
                            class="normal-case flex items-center justify-center w-9 h-9"
                            color="gray"
                            size="md"
                            square
                            @click="
                                sideView =
                                    sideView === 'template' ? null : 'template'
                            "
                        >
                            <FontAwesomeLayers fixed-width>
                                <FontAwesomeIcon :icon="faStar" />
                                <FontAwesomeIcon
                                    v-if="sideView !== 'template'"
                                    :icon="faSlash"
                                />
                            </FontAwesomeLayers>
                        </UButton>
                    </UTooltip>

                    <UTooltip
                        :popper="{ placement: 'top' }"
                        text="Manage Languages"
                    >
                        <UButton
                            :disabled="store.saving"
                            class="normal-case flex items-center justify-center w-9 h-9"
                            color="gray"
                            size="md"
                            square
                            @click="
                                sideView =
                                    sideView === 'language' ? null : 'language'
                            "
                        >
                            <FontAwesomeLayers fixed-width>
                                <FontAwesomeIcon :icon="faLanguage" />
                                <FontAwesomeIcon
                                    v-if="sideView !== 'language'"
                                    :icon="faSlash"
                                />
                            </FontAwesomeLayers>
                        </UButton>
                    </UTooltip>
                </UButtonGroup>
            </div>
        </template>
        <NuxtErrorBoundary
            v-if="['mjml', 'html', 'plain'].includes(store.mail.type)"
        >
            <template #error="{ error }">
                <UAlert color="red" :title="error.message" />
            </template>
            <LazyMailMjmlBuilder v-if="store.mail.type === 'mjml'" />
            <LazyMailHtmlBuilder v-else-if="store.mail.type === 'html'" />
            <LazyMailPlainBuilder v-else />
        </NuxtErrorBoundary>
        <div
            v-else
            class="relative w-full h-full overflow-hidden flex items-center justify-center bg-hd-neutral-50 p-4"
        >
            <div class="text-sm text-hd-neutral-400">
                This email type is not supported yet.
            </div>
        </div>
        <AppFieldList
            v-model:deletable="deletable"
            v-model:editable="editingField"
            :show="sideView === 'fields'"
            :fields="store.builderFields"
            creatable
            @update:show="sideView = null"
            @created="created"
            @saved="update"
        />
        <MailTestModal
            :id="store.mail.id"
            v-model:show="showTestModal"
            :before-send="onBeforeSend"
        />
        <UIModalDelete
            v-model:open="deletable"
            :items="deletable ? [deletable] : []"
            :object="['field', 'field']"
            by="label"
            @delete="deleteField"
        />
        <AppPreviewModal
            v-model:show="preview"
            v-model:locale="previewModalLocale"
            :name="previewModalEmail?.name || store.mail.name"
            :copy-src="
                $api.mail_templates.preview_url_locale(
                    store.mail.id,
                    previewModalLocale?.code ?? '',
                )
            "
            :src="
                $api.mail_templates.preview_url_locale(
                    store.mail.id,
                    previewModalLocale?.code ?? '',
                )
            "
            :language-props="{
                size: 'md',
                allowedLocales: [
                    ...(store.translations?.map((t) => t.locale?.id) ?? []),
                    store.locale?.id ?? user?.account?.locale?.id,
                ].filter((v) => !!v),
            }"
        />
        <MailTemplateDrawer
            v-if="sideView === 'template'"
            :show="sideView === 'template'"
            @update:show="sideView = null"
        />
        <MailLocalizationDrawer
            v-if="sideView === 'language'"
            :show="sideView === 'language'"
            @update:show="sideView = null"
        />
    </AppBuilderPage>
</template>
